/**
 * Status-condition icon set — the literal Figma vectors.
 *
 * Source: Figma "Arcane Circle - Site Design" (file lMwLvgjOUfy4f9e4inZlcG),
 * status cluster node 3242-425. Each icon is an 8x8 line-rounded glyph stroked
 * in mint (#21EDD2) on a dark (#19103D) chip. Path data below is exported
 * verbatim from those nodes; only the chip background rect, clip wrappers and
 * Figma layer ids were stripped, and the stroke set to `currentColor` so the
 * same markup serves both the PIXI canvas (token chips) and the DOM picker.
 *
 * The Figma file specs the icon STYLE and a 7-glyph sample set; it does NOT
 * map specific D&D conditions to glyphs. STATUS_ICON_NAME is a best-fit
 * assignment over that set — adjust freely, it is the single source of truth.
 */
import * as PIXI from 'pixi.js'

/** Mint stroke from the design tokens (--ac-mint-primary). */
export const STATUS_ICON_MINT = '#21EDD2'

/** White stroke used for on-token control-grip glyphs (rotate/lightning/arrow). */
export const STATUS_ICON_WHITE = '#FFFFFF'

/**
 * Lightning bolt — the on-token status-button glyph. Verbatim Figma vector from
 * node 3218:1343 (8x8 line-rounded, stroke=currentColor). This is the GRIP icon
 * (white on the dark chip), kept separate from the mint condition glyphs above.
 */
export const STATUS_GRIP_LIGHTNING_SVG =
  '<path d="M4.74064 0.666672L1.82213 4.06519C1.7992 4.09213 1.78448 4.12508 1.77969 4.16013C1.7749 4.19518 1.78025 4.23086 1.79511 4.26297C1.80997 4.29507 1.83371 4.32225 1.86352 4.34128C1.89334 4.36032 1.92799 4.37041 1.96336 4.37038H3.25916V7.33334L6.17768 3.93482C6.20058 3.90791 6.2153 3.87501 6.2201 3.84001C6.22491 3.805 6.2196 3.76935 6.2048 3.73727C6.19 3.70518 6.16634 3.678 6.13659 3.65893C6.10685 3.63986 6.07227 3.62969 6.03694 3.62963L4.74064 3.62963V0.666672Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>'

/**
 * Inner SVG markup per icon, 8x8 viewBox, stroke=currentColor. Verbatim Figma
 * path data (see file header).
 */
export const STATUS_ICON_SVG: Record<string, string> = {
  heart:
    '<path d="M4.00014 6.91639L1.15936 3.95354C0.909687 3.70407 0.744902 3.38232 0.68837 3.03394C0.631839 2.68555 0.686431 2.3282 0.844405 2.01258C0.963528 1.7744 1.13754 1.56794 1.3521 1.4102C1.56667 1.25246 1.81564 1.14796 2.07851 1.10531C2.34138 1.06266 2.61062 1.08309 2.86405 1.1649C3.11747 1.24671 3.34784 1.38756 3.53615 1.57586L4.00014 2.03954L4.46412 1.57586C4.65243 1.38756 4.8828 1.24671 5.13623 1.1649C5.38965 1.08309 5.6589 1.06266 5.92176 1.10531C6.18463 1.14796 6.43361 1.25246 6.64817 1.4102C6.86273 1.56794 7.03674 1.7744 7.15587 2.01258C7.31363 2.32808 7.36813 2.68521 7.31166 3.0334C7.25518 3.38159 7.0906 3.70319 6.84121 3.95266L4.00014 6.91639Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>',
  star:
    '<path d="M2.80215 2.67927L3.71373 0.843479C3.74027 0.79035 3.78108 0.745665 3.83159 0.714432C3.8821 0.6832 3.94031 0.666656 3.9997 0.666656C4.05909 0.666656 4.1173 0.6832 4.16781 0.714432C4.21832 0.745665 4.25913 0.79035 4.28566 0.843479L5.19725 2.67927L7.23514 2.97541C7.29392 2.98356 7.34923 3.00807 7.39475 3.04615C7.44026 3.08423 7.47416 3.13434 7.49256 3.19075C7.51096 3.24717 7.51314 3.30763 7.49883 3.36522C7.48452 3.42281 7.4543 3.47522 7.41163 3.51646L5.93725 4.94454L6.28532 6.96208C6.32988 7.22103 6.05619 7.41822 5.82216 7.29612L3.9997 6.34314L2.17689 7.29612C1.94321 7.41858 1.66952 7.22103 1.71408 6.96173L2.06215 4.94419L0.587765 3.51611C0.545304 3.47484 0.515273 3.42249 0.501085 3.365C0.486898 3.30751 0.489123 3.24719 0.507508 3.1909C0.525893 3.13461 0.5597 3.08461 0.605087 3.04658C0.650474 3.00855 0.70562 2.98401 0.764256 2.97576L2.80215 2.67927Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>',
  eye:
    '<path d="M3.91655 1.71316C3.69179 1.71034 3.46763 1.73723 3.24992 1.79316C2.279 2.0364 1.34245 2.79518 0.710484 3.56789C0.628329 3.67233 0.583664 3.80136 0.583664 3.93424C0.583664 4.06712 0.628329 4.19615 0.710484 4.30059C1.33268 5.06026 2.26685 5.83237 3.2511 6.07829C3.46851 6.13364 3.69223 6.16024 3.91655 6.15739C4.14127 6.16033 4.3654 6.13373 4.58319 6.07829C5.56744 5.83326 6.5025 5.06085 7.12351 4.30059C7.20567 4.19615 7.25033 4.06712 7.25033 3.93424C7.25033 3.80136 7.20567 3.67233 7.12351 3.56789C6.49124 2.79489 5.55322 2.03611 4.58319 1.79286C4.36545 1.73708 4.1413 1.71029 3.91655 1.71316Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><circle cx="3.91681" cy="3.85192" r="0.647601" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  x: '<path d="M6 2L2 6M2 2L6 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>',
  umbrella:
    '<path d="M5.99999 6.33332C5.99999 6.59854 5.89464 6.85289 5.7071 7.04043C5.51957 7.22797 5.26521 7.33332 4.99999 7.33332C4.73478 7.33332 4.48042 7.22797 4.29289 7.04043C4.10535 6.85289 3.99999 6.59854 3.99999 6.33332L3.99999 3.99999M7.66666 3.99999C7.57959 3.08785 7.15567 2.24091 6.47767 1.62454C5.79967 1.00818 4.91628 0.666656 3.99999 0.666656C3.08371 0.666656 2.20031 1.00818 1.52232 1.62454C0.844318 2.24091 0.420396 3.08785 0.333328 3.99999L7.66666 3.99999Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>',
  wind:
    '<path d="M3.19667 1.53C3.27411 1.45211 3.36959 1.39455 3.47463 1.36245C3.57967 1.33035 3.69101 1.3247 3.79876 1.346C3.90651 1.3673 4.00733 1.4149 4.09226 1.48455C4.17718 1.55421 4.24358 1.64377 4.28555 1.74527C4.32752 1.84677 4.34376 1.95707 4.33283 2.06636C4.3219 2.17565 4.28413 2.28055 4.22289 2.37172C4.16165 2.4629 4.07883 2.53754 3.98179 2.58899C3.88475 2.64045 3.77651 2.66713 3.66667 2.66667L0.666672 2.66667M4.19667 6.47C4.27411 6.5479 4.36959 6.60546 4.47463 6.63756C4.57967 6.66966 4.69101 6.67531 4.79876 6.65401C4.90651 6.63271 5.00733 6.58511 5.09226 6.51546C5.17718 6.4458 5.24358 6.35624 5.28555 6.25474C5.32752 6.15324 5.34376 6.04294 5.33283 5.93365C5.3219 5.82436 5.28413 5.71946 5.22289 5.62828C5.16165 5.53711 5.07883 5.46247 4.98179 5.41102C4.88475 5.35956 4.77651 5.33288 4.66667 5.33334H0.666672M5.91 2.57667C6.00695 2.47997 6.12624 2.40864 6.25731 2.36901C6.38839 2.32937 6.52721 2.32265 6.6615 2.34943C6.79579 2.37622 6.92141 2.43568 7.02725 2.52256C7.13309 2.60944 7.21589 2.72107 7.26833 2.84757C7.32077 2.97407 7.34122 3.11154 7.32789 3.24782C7.31455 3.3841 7.26784 3.515 7.19188 3.62894C7.11592 3.74287 7.01305 3.83633 6.89238 3.90105C6.7717 3.96577 6.63694 3.99976 6.5 4L0.666672 4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>',
  triangle:
    '<path d="M3.43001 1.28667L0.606674 6.00001C0.548463 6.10081 0.517663 6.21511 0.517337 6.33151C0.517011 6.44792 0.547171 6.56238 0.604816 6.66352C0.662461 6.76465 0.745582 6.84892 0.84591 6.90796C0.946237 6.96699 1.06027 6.99873 1.17667 7.00001L6.82334 7.00001C6.93974 6.99873 7.05378 6.96699 7.1541 6.90796C7.25443 6.84892 7.33755 6.76465 7.3952 6.66352C7.45284 6.56238 7.483 6.44792 7.48268 6.33151C7.48235 6.21511 7.45155 6.10081 7.39334 6.00001L4.57001 1.28667C4.51058 1.18871 4.42691 1.10771 4.32707 1.0515C4.22723 0.99529 4.11459 0.965759 4.00001 0.965759C3.88543 0.965759 3.77278 0.99529 3.67294 1.0515C3.5731 1.10771 3.48943 1.18871 3.43001 1.28667Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>',
}

/** Fallback glyph for an unmapped condition. */
const FALLBACK_ICON = 'triangle'

/**
 * Best-fit condition -> Figma glyph assignment (see file header — the Figma
 * does not spec this mapping; it is ours to set).
 */
export const STATUS_ICON_NAME: Record<string, string> = {
  charmed: 'heart',
  blessed: 'star',
  blinded: 'eye',
  grappled: 'x',
  restrained: 'x',
  unconscious: 'umbrella',
  poisoned: 'wind',
  prone: 'triangle',
  stunned: 'star',
  frightened: 'triangle',
  concentrating: 'eye',
  burning: 'triangle',
}

/** Resolve a status key to its Figma glyph name (falls back to a neutral one). */
export function statusIconName(statusKey: string): string {
  return STATUS_ICON_NAME[statusKey] ?? FALLBACK_ICON
}

/**
 * Build a standalone SVG string for an icon at a given stroke color. Default
 * 32px (4x the 8-unit viewBox) keeps the rasterised PIXI texture crisp; the DOM
 * picker passes a smaller size to match its chips.
 */
export function statusIconSvg(name: string, color: string, size = 32): string {
  const inner = STATUS_ICON_SVG[name] ?? STATUS_ICON_SVG[FALLBACK_ICON]
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 8 8" fill="none" style="color:${color}">${inner}</svg>`
}

/**
 * Standalone SVG for the lightning grip glyph at a given stroke color/size.
 * Mirrors statusIconSvg but draws the grip vector (which lives outside the
 * mint condition set). Default 32px keeps the rasterised texture crisp.
 */
export function lightningGripSvg(color: string = STATUS_ICON_WHITE, size = 32): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 8 8" fill="none" style="color:${color}">${STATUS_GRIP_LIGHTNING_SVG}</svg>`
}

const textureCache = new Map<string, PIXI.Texture>()

/**
 * PIXI texture for the lightning grip glyph (white by default). Cached so the
 * per-selection-render redraw doesn't re-rasterise it each pass.
 */
export function lightningGripTexture(
  color: string = STATUS_ICON_WHITE
): PIXI.Texture {
  const cacheKey = `__grip_lightning:${color}`
  const cached = textureCache.get(cacheKey)
  if (cached) return cached
  const url = `data:image/svg+xml;base64,${btoa(lightningGripSvg(color))}`
  const texture = PIXI.Texture.from(url)
  textureCache.set(cacheKey, texture)
  return texture
}

/**
 * PIXI texture for a status image icon (registry `iconUrl` art under
 * /status-icons/). Same cache as the glyph textures so the per-render-pass
 * token redraw reuses one texture per URL. Uses PIXI.Texture.from — the
 * texture loads lazily and the sprite resolves its set width/height once the
 * base texture arrives (Sprite preserves explicit _width/_height across
 * texture updates), matching how the SVG data-URL glyphs load here.
 */
export function statusImageTexture(url: string): PIXI.Texture {
  const cacheKey = `__img:${url}`
  const cached = textureCache.get(cacheKey)
  if (cached) return cached
  const texture = PIXI.Texture.from(url)
  textureCache.set(cacheKey, texture)
  return texture
}

/**
 * PIXI texture for a status glyph, mint by default. Cached by name+color so the
 * per-render-pass token redraw doesn't re-rasterise the same icon repeatedly.
 */
export function statusIconTexture(
  name: string,
  color: string = STATUS_ICON_MINT
): PIXI.Texture {
  const cacheKey = `${name}:${color}`
  const cached = textureCache.get(cacheKey)
  if (cached) return cached
  const url = `data:image/svg+xml;base64,${btoa(statusIconSvg(name, color))}`
  const texture = PIXI.Texture.from(url)
  textureCache.set(cacheKey, texture)
  return texture
}
